import type { PermissionLevel } from '@datapad/data-model';
import { Logout } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import type React from 'react';
import { menuHorizontalPadding, menuVerticalPadding } from './Constants';
import { ViewAsSelector } from './ViewAsSelector';

/**
 * {@link MenuFooter} input props.
 */
export interface MenuFooterProps {
	/**
	 * Log the session user out.
	 */
	logout(): void;

	/**
	 * Whether the current user is actually a Game Master (for showing View As selector).
	 */
	isGameMaster: boolean;

	/**
	 * The current effective permission level.
	 */
	effectivePermissionLevel: PermissionLevel;

	/**
	 * Callback to change the effective permission level for "View As" mode.
	 */
	setEffectivePermissionLevel(level: PermissionLevel | undefined): void;
}

/**
 * Datapad menu footer containing the View As selector (for GMs) and logout button.
 *
 * @remarks
 * Uses an outlined button variant for a less prominent appearance,
 * since logout is not a primary action.
 */
export function MenuFooter(props: MenuFooterProps): React.ReactElement {
	const { logout, isGameMaster, effectivePermissionLevel, setEffectivePermissionLevel } = props;

	return (
		<Box
			sx={{
				width: '100%',
				boxSizing: 'border-box',
			}}
		>
			{/* View As selector (GM only) */}
			{isGameMaster && (
				<ViewAsSelector
					currentEffectiveLevel={effectivePermissionLevel}
					onChange={setEffectivePermissionLevel}
				/>
			)}

			{/* Logout button */}
			<Box sx={{ px: menuHorizontalPadding, py: menuVerticalPadding }}>
				{/*
				 * Outlined variant is used for secondary actions.
				 * Full width provides a larger touch target.
				 */}
				<Button
					variant="outlined"
					color="inherit"
					onClick={logout}
					startIcon={<Logout />}
					fullWidth
					sx={{
						justifyContent: 'flex-start',
						color: 'text.secondary',
						borderColor: 'divider',
						'&:hover': {
							borderColor: 'text.secondary',
							bgcolor: 'action.hover',
						},
					}}
				>
					Log Out
				</Button>
			</Box>
		</Box>
	);
}
